/**
 * Game-over brag card (FRVR shareMessage image), drawn in the cartoon style: a cel sky in
 * hard-edged bands with a slow ray burst, a creature on its disc bursting out of a gold
 * starburst, and the score in inked Fredoka lettering on an ink-ringed plate.
 */
import { INK, PUFF_TONES, TONES } from "../palette.js";
import { TAU, inkText, inkStyle, glint } from "../toon-kit.js";
import { drawCreature } from "../creatures/index.js";
import { drawStarburstPlate } from "./overlay-art.js";

/**
 * @param {CanvasRenderingContext2D} c
 * @param {number} w
 * @param {number} h
 * @param {{ score: number, best: number, creature?: string }} o
 */
export function drawShareCard(c, w, h, o) {
  var score = Math.max(0, Math.floor(Number(o && o.score) || 0));
  var best = Math.max(score, Math.floor(Number(o && o.best) || 0));
  c.save();
  // Sky in three hard bands, sea band at the foot.
  c.fillStyle = TONES.sky.light;
  c.fillRect(0, 0, w, h);
  c.fillStyle = TONES.sky.base;
  c.fillRect(0, h * 0.42, w, h);
  c.fillStyle = TONES.sea.base;
  c.fillRect(0, h * 0.8, w, h);
  c.fillStyle = TONES.sea.light;
  c.fillRect(0, h * 0.8, w, h * 0.025);

  // Rays fanning from behind the creature.
  var cx = w * 0.23, cy = h * 0.52;
  c.save();
  c.fillStyle = "rgba(255,255,255,0.22)";
  c.beginPath();
  for (var i = 0; i < 14; i++) {
    var a = (i / 14) * TAU;
    c.moveTo(cx, cy);
    c.arc(cx, cy, w, a, a + TAU / 28);
    c.closePath();
  }
  c.fill();
  c.restore();

  drawStarburstPlate(c, cx, cy, h * 0.4, PUFF_TONES.hot);
  c.save();
  c.translate(cx, cy);
  c.rotate(-0.12);
  drawCreature(c, (o && o.creature) || "dragon_final", h * 0.27, { t: 0, mood: "grin" });
  c.restore();

  // Score plate: cream, ink ring, shade foot, hard ink drop.
  var px = w * 0.46, py = h * 0.33, pw = w * 0.5, ph = h * 0.5, pr = h * 0.09;
  roundRectPath(c, px, py + h * 0.035, pw, ph, pr);
  c.fillStyle = INK;
  c.fill();
  roundRectPath(c, px, py, pw, ph, pr);
  c.fillStyle = TONES.cream.shade;
  c.fill();
  c.save();
  roundRectPath(c, px, py, pw, ph, pr);
  c.clip();
  c.fillStyle = TONES.cream.base;
  c.fillRect(px, py - ph * 0.08, pw, ph * 0.94);
  c.fillStyle = "#ffffff";
  c.fillRect(px, py, pw, ph * 0.18);
  c.restore();
  roundRectPath(c, px, py, pw, ph, pr);
  inkStyle(c, h * 0.022, INK);
  c.stroke();
  glint(c, px + pw * 0.12, py + ph * 0.14, h * 0.02);

  inkText(c, "Catch'n'Merge", px + pw / 2, h * 0.17, h * 0.12, "#ffffff", { stroke: 0.26 });
  inkText(c, "SCORE", px + pw / 2, py + ph * 0.24, h * 0.075, TONES.sky.shade, { stroke: 0.22 });
  inkText(c, String(score), px + pw / 2, py + ph * 0.56, h * 0.19, "#ffffff", { stroke: 0.22 });
  inkText(c, "BEST " + best, px + pw / 2, py + ph * 0.86, h * 0.08, TONES.gold.base, { stroke: 0.26 });

  // Ink frame round the card.
  roundRectPath(c, h * 0.012, h * 0.012, w - h * 0.024, h - h * 0.024, h * 0.06);
  inkStyle(c, h * 0.024, INK);
  c.stroke();
  c.restore();
}

/** Rounded rectangle path (arcTo, for canvases without roundRect). */
export function roundRectPath(c, x, y, w, h, r) {
  var rr = Math.min(r, w / 2, h / 2);
  c.beginPath();
  c.moveTo(x + rr, y);
  c.arcTo(x + w, y, x + w, y + h, rr);
  c.arcTo(x + w, y + h, x, y + h, rr);
  c.arcTo(x, y + h, x, y, rr);
  c.arcTo(x, y, x + w, y, rr);
  c.closePath();
}
